// Bonus calculation rules (Cauver Bonus spec v1.6, B2 rounding, B4 ledger
// allocation, B6 calculation, B7 formula). Pure functions: the sample API
// (bonusSampleApi.js) runs them until the backend's calculation engine
// (POST pay-inputs/bonus/runs/calculate/) is built.

// ---------- Months ("YYYY-MM") ----------

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const monthKey = (year, monthIndex) => `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
const parseMonth = (key) => {
  const [year, month] = String(key).split("-").map(Number);
  return { year, index: month - 1 };
};
export const addMonths = (key, count) => {
  const { year, index } = parseMonth(key);
  const total = year * 12 + index + count;
  return monthKey(Math.floor(total / 12), total % 12);
};
export const monthsBetween = (from, to) => {
  if (!from || !to || from > to) return [];
  const result = [];
  for (let key = from; key <= to; key = addMonths(key, 1)) result.push(key);
  return result;
};
export const monthCount = (from, to) => monthsBetween(from, to).length;
// "Apr 25"
export const shortMonth = (key) => {
  const { year, index } = parseMonth(key);
  return `${MONTH_NAMES[index]} ${String(year).slice(2)}`;
};
// "Apr 2025"
export const longMonth = (key) => {
  const { year, index } = parseMonth(key);
  return `${MONTH_NAMES[index]} ${year}`;
};
// "Apr 2025 – Mar 2026", or one month.
export const periodText = (from, to) => (from === to ? longMonth(from) : `${longMonth(from)} – ${longMonth(to)}`);
export const daysInMonth = (key) => {
  const { year, index } = parseMonth(key);
  return new Date(year, index + 1, 0).getDate();
};
export const monthOfDate = (date) => String(date || "").slice(0, 7);

// Default From / To for a bonus type's default period, ending at `today`.
export const defaultMonths = (period, today = new Date()) => {
  const current = monthKey(today.getFullYear(), today.getMonth());
  const { year, index } = parseMonth(current);
  if (period === "fy") {
    // Last complete financial year (Apr–Mar).
    const endYear = index >= 3 ? year : year - 1;
    return { from: `${endYear - 1}-04`, to: `${endYear}-03` };
  }
  if (period === "cy") return { from: `${year - 1}-01`, to: `${year - 1}-12` };
  const last = addMonths(current, -1);
  return { from: last, to: last };
};

// ---------- Rounding (spec B2: fixed in code, half up) ----------

const EPS = 1e-9;
export const roundHalfUp2 = (value) => Math.floor(Number(value) * 100 + 0.5 + EPS) / 100;
export const roundAmount = (value, mode = "NEAREST") => {
  const x = Number(value) || 0;
  switch (mode) {
    case "UP":
      return Math.ceil(roundHalfUp2(x) - EPS);
    case "DOWN":
      return Math.floor(x + EPS);
    case "NONE":
      return roundHalfUp2(x);
    case "NEAREST":
    default:
      return Math.floor(x + 0.5 + EPS);
  }
};

// ---------- Ledger allocation (spec B4 BonusPaidMonth) ----------

// Split `amount` across months in proportion to weights (bonus(m)), to 2
// decimals; the last month takes the rounding difference. All-zero weights
// split evenly.
export const allocate = (amount, months, weights = {}) => {
  if (!months.length) return {};
  const total = months.reduce((sum, m) => sum + (Number(weights[m]) || 0), 0);
  const result = {};
  let used = 0;
  months.forEach((m, i) => {
    if (i === months.length - 1) {
      result[m] = roundHalfUp2(amount - used);
      return;
    }
    const share = total > 0 ? ((Number(weights[m]) || 0) / total) * amount : amount / months.length;
    result[m] = roundHalfUp2(share);
    used = roundHalfUp2(used + result[m]);
  });
  return result;
};

// Earlier bonus for one employee + type over `months`, from entries that are
// Ready / Included / Processed (cancelled never count). Only months inside
// the run count.
export const earlierBonus = (entries, employeeId, bonusTypeId, months) => {
  const inside = new Set(months);
  let paid = 0;
  let waiting = 0;
  const runs = new Set();
  entries.forEach((entry) => {
    if (entry.employee_id !== employeeId || entry.bonus_type_id !== bonusTypeId || entry.status === "cancelled") return;
    let part = 0;
    Object.entries(entry.allocations || {}).forEach(([m, value]) => { if (inside.has(m)) part += Number(value) || 0; });
    if (part <= 0) return;
    if (entry.status === "processed") paid += part; else waiting += part;
    runs.add(entry.run_id || entry.id);
  });
  return { total: roundHalfUp2(paid + waiting), paid: roundHalfUp2(paid), waiting: roundHalfUp2(waiting), sources: [...runs] };
};

// ---------- Formula (spec B7) ----------

const FUNCTIONS = ["IF", "MIN", "MAX", "ROUND"];

class FormulaError extends Error {}

const tokenize = (text) => {
  const tokens = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (/\s/.test(ch)) { i += 1; continue; }
    if (ch === "[") {
      const end = text.indexOf("]", i);
      if (end < 0) throw new FormulaError(`Brackets don't match (at character ${i + 1})`);
      tokens.push({ type: "name", value: text.slice(i + 1, end).trim(), pos: i + 1 });
      i = end + 1;
      continue;
    }
    const num = /^\d+(\.\d+)?/.exec(text.slice(i));
    if (num) { tokens.push({ type: "num", value: Number(num[0]), pos: i + 1 }); i += num[0].length; continue; }
    const word = /^[A-Za-z_]+/.exec(text.slice(i));
    if (word) { tokens.push({ type: "fn", value: word[0].toUpperCase(), pos: i + 1 }); i += word[0].length; continue; }
    const op = /^(>=|<=|<>|!=|==|[-+*/×÷(),<>=])/.exec(text.slice(i));
    if (op) {
      const value = { "×": "*", "÷": "/", "==": "=", "!=": "<>" }[op[0]] || op[0];
      tokens.push({ type: "op", value, pos: i + 1 });
      i += op[0].length;
      continue;
    }
    throw new FormulaError(`A value is missing here (at character ${i + 1})`);
  }
  return tokens;
};

// Recursive-descent parser -> AST. No eval of code.
const parse = (text) => {
  const tokens = tokenize(text);
  let p = 0;
  const peek = () => tokens[p];
  const take = (value) => {
    const token = tokens[p];
    if (!token || token.value !== value) throw new FormulaError(`Expected "${value}" (at character ${token ? token.pos : text.length + 1})`);
    p += 1;
    return token;
  };
  const primary = () => {
    const token = peek();
    if (!token) throw new FormulaError("A value is missing here (at the end)");
    if (token.type === "num") { p += 1; return { t: "num", v: token.value }; }
    if (token.type === "name") { p += 1; return { t: "name", v: token.value, pos: token.pos }; }
    if (token.value === "-") { p += 1; return { t: "neg", a: primary() }; }
    if (token.value === "(") { p += 1; const e = comparison(); take(")"); return e; }
    if (token.type === "fn") {
      if (!FUNCTIONS.includes(token.value)) throw new FormulaError(`Unknown function ${token.value} (at character ${token.pos})`);
      p += 1;
      take("(");
      const args = [];
      if (peek()?.value !== ")") {
        args.push(comparison());
        while (peek()?.value === ",") { p += 1; args.push(comparison()); }
      }
      take(")");
      if (token.value === "IF" && args.length !== 3) throw new FormulaError("IF needs a condition, a value if true and a value if false");
      if (token.value === "ROUND" && (args.length < 1 || args.length > 2)) throw new FormulaError("Wrong number of values in ROUND()");
      if ((token.value === "MIN" || token.value === "MAX") && args.length < 1) throw new FormulaError(`Wrong number of values in ${token.value}()`);
      return { t: "fn", v: token.value, args };
    }
    throw new FormulaError(`A value is missing here (at character ${token.pos})`);
  };
  const term = () => {
    let left = primary();
    while (peek() && (peek().value === "*" || peek().value === "/")) {
      const op = peek().value; p += 1;
      left = { t: "bin", op, a: left, b: primary() };
    }
    return left;
  };
  const sumExpr = () => {
    let left = term();
    while (peek() && (peek().value === "+" || peek().value === "-")) {
      const op = peek().value; p += 1;
      left = { t: "bin", op, a: left, b: term() };
    }
    return left;
  };
  const comparison = () => {
    const left = sumExpr();
    const token = peek();
    if (token && [">", "<", ">=", "<=", "=", "<>"].includes(token.value)) {
      p += 1;
      return { t: "cmp", op: token.value, a: left, b: sumExpr() };
    }
    return left;
  };
  const ast = comparison();
  if (p < tokens.length) throw new FormulaError(`An operator is missing between two values (at character ${tokens[p].pos})`);
  return ast;
};

// Evaluate with lazy names: resolve(name) is called only for names on the
// branch taken (IF evaluates only the chosen branch). resolve throws when a
// value is missing, so it becomes Need checking, never 0.
const evaluate = (node, resolve) => {
  switch (node.t) {
    case "num": return node.v;
    case "name": return resolve(node.v);
    case "neg": return -evaluate(node.a, resolve);
    case "bin": {
      const a = evaluate(node.a, resolve);
      const b = evaluate(node.b, resolve);
      if (node.op === "+") return a + b;
      if (node.op === "-") return a - b;
      if (node.op === "*") return a * b;
      if (b === 0) throw new FormulaError("Division by zero");
      return a / b;
    }
    case "cmp": {
      const a = evaluate(node.a, resolve);
      const b = evaluate(node.b, resolve);
      return { ">": a > b, "<": a < b, ">=": a >= b, "<=": a <= b, "=": a === b, "<>": a !== b }[node.op] ? 1 : 0;
    }
    case "fn": {
      if (node.v === "IF") return evaluate(node.args[0], resolve) ? evaluate(node.args[1], resolve) : evaluate(node.args[2], resolve);
      const values = node.args.map((arg) => evaluate(arg, resolve));
      if (node.v === "MIN") return Math.min(...values);
      if (node.v === "MAX") return Math.max(...values);
      const digits = values[1] ?? 0;
      return Math.floor(values[0] * 10 ** digits + 0.5 + EPS) / 10 ** digits;
    }
    default: return 0;
  }
};

class MissingValue extends Error {}

// ---------- Per-employee calculation (spec B6) ----------

const fmt = (value) => `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Map the type's wage component names onto the sample salary fields. (The
// backend reads each component's earned amount from finalised payroll.)
const componentField = (name) => {
  const text = String(name || "").toLowerCase();
  if (text === "gross" || text.includes("gross")) return "gross";
  if (text.includes("overtime") || /\bot\b/.test(text)) return "ot";
  if (text.includes("basic")) return "basic";
  if (text === "da" || text.includes("dearness")) return "da";
  return null;
};

export const wageNames = (type) => {
  const names = type.wage_component_names || [];
  if (names.length) return names;
  return (type.wage_components || []).map((id) => (id === "GROSS" ? "Gross" : String(id)));
};

// Sample data only knows Basic, DA, Gross and OT; a type whose components
// map to none of them is read as Basic + DA.
const earnedFor = (month, type) => {
  const fields = wageNames(type).map(componentField).filter(Boolean);
  const used = fields.length ? fields : ["basic", "da"];
  return used.reduce((sum, field) => sum + (Number(month[field]) || 0), 0);
};

export const employedIn = (employee, key) => {
  const first = `${key}-01`;
  const last = `${key}-${String(daysInMonth(key)).padStart(2, "0")}`;
  return (!employee.joined || employee.joined <= last) && (!employee.exited || employee.exited >= first);
};

const result = (base, extra) => ({ ...base, ...extra });

// Returns one BonusRunEmployee row with its BonusRunMonth rows:
// { result: eligible | excluded | check, reason, months: [...], due, ... }
export const calculateEmployee = ({ employee, type, from, to, payMode, partPercent, rounding, entries }) => {
  const months = monthsBetween(from, to);
  const employed = months.filter((m) => employedIn(employee, m));
  const daysBasis = type.days_basis || "paid_days";
  const base = {
    employee_id: employee.id,
    employee_name: employee.name,
    employee_code: employee.code,
    site: employee.site,
    department: employee.department,
    days_basis: daysBasis,
    months_counted: employed.length,
    employed_months: employed,
    months: [],
    days_counted_total: null,
    wages_used_total: null,
    due_unrounded: null,
    due: null,
    already_paid: null,
    paid_in_salary: null,
    waiting_in_payroll: null,
    pay_now: null,
    reason: "",
  };

  if (!employed.length) {
    const joinedAfter = employee.joined && employee.joined > `${to}-31`;
    return result(base, {
      result: "excluded",
      reason_code: joinedAfter ? "JOINED_AFTER" : employee.exited && employee.exited < `${from}-01` ? "EXITED_BEFORE" : "NO_EMPLOYED_MONTHS",
      reason: joinedAfter ? "Joined after period" : employee.exited && employee.exited < `${from}-01` ? "Exited before period" : "No employed months in period",
    });
  }

  const data = (m) => employee.monthly[m] || {};
  const salaryReady = (m) => data(m).payrollFinal !== false && employee.monthly[m];
  const check = (code, reason) => result(base, { result: "check", reason_code: code, reason });

  const method = type.method;
  const usesWages = method === "percent_of_wages";
  const needsDays = method === "per_day" || (type.min_days != null && type.min_days !== "");
  const wageLimit = type.wage_limit != null && type.wage_limit !== "" ? Number(type.wage_limit) : null;

  // 2. Load only the inputs this method and its rules need.
  for (const m of employed) {
    if ((usesWages || wageLimit != null || (needsDays && daysBasis === "paid_days")) && !salaryReady(m)) {
      return check("SALARY_MISSING", `Salary missing — ${shortMonth(m)} payroll not finalised`);
    }
    if (needsDays && daysBasis === "days_present") {
      if (!employee.monthly[m]) return check("ATTENDANCE_MISSING", `Attendance missing for ${shortMonth(m)}`);
      if (data(m).attendanceComplete === false) return check("ATTENDANCE_INCOMPLETE", `Attendance not complete for ${shortMonth(m)}`);
    }
  }

  const daysOf = (m) => Number(daysBasis === "days_present" ? data(m).daysPresent : data(m).paidDays) || 0;
  const daysTotal = needsDays ? employed.reduce((sum, m) => sum + daysOf(m), 0) : null;

  // 3. Eligibility: every enabled rule must pass.
  if (wageLimit != null) {
    const over = employed.find((m) => Number(data(m).gross) > wageLimit);
    if (over) {
      return result(base, {
        result: "excluded",
        reason_code: "WAGE_ABOVE_LIMIT",
        reason: `Gross earned ${fmt(data(over).gross)} (${shortMonth(over)}) > ${fmt(wageLimit)}`,
        days_counted_total: daysTotal,
      });
    }
  }
  if (type.min_days != null && type.min_days !== "" && daysTotal < Number(type.min_days)) {
    return result(base, {
      result: "excluded",
      reason_code: "BELOW_MIN_DAYS",
      reason: `${daysBasis === "days_present" ? "Days present" : "Paid days"} ${daysTotal} (min ${Number(type.min_days)})`,
      days_counted_total: daysTotal,
    });
  }

  // 4. Amount by method, month by month (unrounded).
  const monthRows = months.map((m) => ({
    month: m,
    employed: employed.includes(m),
    paid_days: employed.includes(m) ? Number(data(m).paidDays) || 0 : null,
    days_present: employed.includes(m) ? Number(data(m).daysPresent) || 0 : null,
    earned_wage: null,
    gross_earned: employed.includes(m) && employee.monthly[m] ? Number(data(m).gross) || 0 : null,
    wages_used: null,
    bonus_unrounded: null,
  }));
  let total = 0;
  let toMonthNote = "";

  if (method === "percent_of_wages") {
    const ceiling = type.ceiling != null && type.ceiling !== "" ? Number(type.ceiling) : null;
    monthRows.forEach((row) => {
      if (!row.employed) return;
      row.earned_wage = earnedFor(data(row.month), type);
      row.wages_used = ceiling != null ? Math.min(row.earned_wage, ceiling) : row.earned_wage;
      row.bonus_unrounded = (row.wages_used * Number(type.rate)) / 100;
      total += row.bonus_unrounded;
    });
  } else if (method === "per_day") {
    monthRows.forEach((row) => {
      if (!row.employed) return;
      row.bonus_unrounded = Number(type.per_day_amount) * daysOf(row.month);
      total += row.bonus_unrounded;
    });
  } else if (method === "fixed_or_days") {
    if (type.calculation_basis === "days_wages") {
      if (!employed.includes(to)) return check("NOT_EMPLOYED_TO_MONTH", `Not employed in ${shortMonth(to)} — days' wages uses the To month`);
      if (!salaryReady(to)) return check("SALARY_MISSING", `Salary missing — ${shortMonth(to)} payroll not finalised`);
      const divisor = type.day_divisor === "actual" ? daysInMonth(to) : Number(type.day_divisor);
      const earned = earnedFor(data(to), type);
      total = (Number(type.days) * earned) / divisor;
      const row = monthRows.find((r) => r.month === to);
      row.earned_wage = earned;
      row.wages_used = earned;
      row.bonus_unrounded = total;
      toMonthNote = `Uses ${shortMonth(to)} wages ${fmt(earned)} ÷ ${divisor}`;
    } else {
      total = Number(type.fixed_amount);
    }
  } else if (method === "formula") {
    const totals = (field) => employed.reduce((sum, m) => sum + (Number(data(m)[field]) || 0), 0);
    const resolve = (name) => {
      const key = String(name).toLowerCase();
      if (key === "months worked") return employed.length;
      if (key === "days present" || key === "paid days") {
        const field = key === "days present" ? "daysPresent" : "paidDays";
        const missing = employed.find((m) => !employee.monthly[m] || (field === "daysPresent" && data(m).attendanceComplete === false) || (field === "paidDays" && !salaryReady(m)));
        if (missing) throw new MissingValue(`Formula error — ${employee.name}, ${shortMonth(missing)}: [${name}] has no value`);
        return totals(field);
      }
      if (key === "monthly wage") {
        if (!salaryReady(to)) throw new MissingValue(`Formula error — ${employee.name}, ${shortMonth(to)}: [${name}] has no value`);
        return Number(data(to).gross) || 0;
      }
      const field = componentField(name);
      if (!field) throw new MissingValue(`Formula error — ${employee.name}: [${name}] has no value`);
      const missing = employed.find((m) => !salaryReady(m));
      if (missing) throw new MissingValue(`Formula error — ${employee.name}, ${shortMonth(missing)}: [${name}] has no value`);
      return totals(field);
    };
    try {
      total = evaluate(parse(type.formula || "0"), resolve);
    } catch (error) {
      return check("FORMULA_ERROR", error.message);
    }
    if (!Number.isFinite(total) || total < 0) return check("FORMULA_ERROR", `Formula error — ${employee.name}: result must be 0 or more`);
  }

  // 5. Add the months and round once, on the total.
  const due = roundAmount(total, rounding);
  // 6. Subtract earlier bonus for the same type and overlapping months.
  const earlier = earlierBonus(entries, employee.id, type.id, months);
  const balance = Math.max(0, roundHalfUp2(due - earlier.total));
  const payNow = payMode === "part"
    ? Math.min(roundAmount((due * Number(partPercent)) / 100, rounding), balance)
    : balance;

  const done = {
    months: monthRows,
    days_counted_total: daysTotal ?? (method === "percent_of_wages" ? employed.reduce((sum, m) => sum + daysOf(m), 0) : null),
    wages_used_total: method === "percent_of_wages" ? monthRows.reduce((sum, r) => sum + (r.wages_used || 0), 0) : null,
    earned_total: method === "percent_of_wages" ? monthRows.reduce((sum, r) => sum + (r.earned_wage || 0), 0) : null,
    due_unrounded: roundHalfUp2(total),
    due,
    already_paid: earlier.total,
    paid_in_salary: earlier.paid,
    waiting_in_payroll: earlier.waiting,
    earlier_sources: earlier.sources,
    pay_now: payNow,
    to_month_note: toMonthNote,
  };

  if (earlier.total > due) {
    return result(base, {
      ...done,
      pay_now: 0,
      result: "check",
      reason_code: "EARLIER_MORE_THAN_DUE",
      reason: `Earlier bonus is more than the current bonus due by ${fmt(roundHalfUp2(earlier.total - due))}. No further payment will be made. Review the difference and use Payroll Adjustments if recovery is needed.`,
    });
  }
  return result(base, { ...done, result: "eligible", reason: payNow === 0 ? "Eligible · nothing to pay" : "" });
};

// Pay-now split across months for the ledger: run entries in proportion to
// bonus(m); fixed amounts (no monthly bonus) evenly over employed months.
export const runAllocation = (row) => {
  const months = row.employed_months || [];
  const weights = Object.fromEntries((row.months || []).map((m) => [m.month, m.bonus_unrounded || 0]));
  return allocate(row.pay_now, months, weights);
};

// Check formula on the client (same rules as the server's check, used only
// where the server is not involved).
export const checkFormulaLocally = (text) => {
  try {
    parse(text);
    return { valid: true, errors: [] };
  } catch (error) {
    return { valid: false, errors: [error.message] };
  }
};
